import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  importFailureMessage,
  sanitizeMapMeta,
  type MapImportResult,
  type MapMeta,
} from '../../sim/map/index.ts';
import { sanitizeMapFilename } from '../../sim/map/filename.ts';
import { setPanel } from '../panels.ts';
import { MapMetaFields } from './mapMeta.tsx';
import './dialog.css';

export type EditorDialogProps = {
  title: string;
  message?: string;
  detail?: string;
  confirmLabel: string;
  cancelLabel?: string;
  inputDefault?: string;
  inputLabel?: string;
  /** Select the default text so typing replaces it. */
  selectOnFocus?: boolean;
  onConfirm: (value: string) => void;
  onCancel: () => void;
};

export type ConfirmOptions = {
  title: string;
  message?: string;
  detail?: string;
  confirmLabel?: string;
  cancelLabel?: string;
};

export type PromptOptions = ConfirmOptions & {
  defaultValue: string;
  inputLabel?: string;
};

export function EditorDialogPanel({
  title,
  message,
  detail,
  confirmLabel,
  cancelLabel,
  inputDefault,
  inputLabel,
  selectOnFocus = false,
  onConfirm,
  onCancel,
}: EditorDialogProps): JSX.Element {
  const [value, setValue] = useState(inputDefault ?? '');
  const hasInput = inputDefault !== undefined;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!selectOnFocus) {
      return;
    }

    const input = inputRef.current;

    if (!input) {
      return;
    }

    input.focus();
    input.select();
  }, [selectOnFocus]);

  const submit = (): void => {
    onConfirm(value);
  };

  return (
    <div class="editor-dialog-backdrop ui-chrome">
      <div class="editor-dialog ui-panel" role="dialog" aria-modal="true" aria-label={title}>
        <h2 class="editor-dialog-title">{title}</h2>
        {message ? <p class="ui-hint">{message}</p> : null}
        {detail ? <p class="editor-dialog-detail">{detail}</p> : null}
        {hasInput ? (
          <label class="editor-dialog-field">
            <span class="ui-hint">{inputLabel ?? 'File name'}</span>
            <input
              ref={inputRef}
              class="ui-number-input"
              type="text"
              name="map-file"
              value={value}
              autofocus={!selectOnFocus}
              onInput={(event) => setValue((event.target as HTMLInputElement).value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  submit();
                }

                if (event.key === 'Escape') {
                  event.preventDefault();
                  event.stopPropagation();
                  onCancel();
                }
              }}
            />
          </label>
        ) : null}
        <div class="editor-dialog-actions">
          {cancelLabel ? (
            <button type="button" class="ui-button" onClick={onCancel}>
              {cancelLabel}
            </button>
          ) : null}
          <button type="button" class="ui-button" autofocus={!hasInput} onClick={submit}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Replaces the blocking `globalThis.confirm` the editor used to open on boot. */
export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const settle = (result: boolean): void => {
      setPanel('editorDialog', null);
      resolve(result);
    };

    setPanel('editorDialog', {
      title: options.title,
      message: options.message,
      detail: options.detail,
      confirmLabel: options.confirmLabel ?? 'Confirm',
      cancelLabel: options.cancelLabel ?? 'Cancel',
      onConfirm: () => settle(true),
      onCancel: () => settle(false),
    });
  });
}

/** One-button status dialog. Escape / Close both dismiss it. */
export function alertDialog(options: {
  title: string;
  message: string;
  confirmLabel?: string;
}): Promise<void> {
  return new Promise((resolve) => {
    const settle = (): void => {
      setPanel('editorDialog', null);
      resolve();
    };

    setPanel('editorDialog', {
      title: options.title,
      message: options.message,
      confirmLabel: options.confirmLabel ?? 'Close',
      onConfirm: settle,
      onCancel: settle,
    });
  });
}

/** Import failure: Close only, or Convert / Fix when the loader offered one field patch. */
export async function reportMapImportFailure(
  result: Extract<MapImportResult, { ok: false }>,
): Promise<boolean> {
  const message = importFailureMessage(result);

  if (!result.fix) {
    await alertDialog({ title: 'Could not import map', message });
    return false;
  }

  return confirmDialog({
    title: 'Could not import map',
    message,
    confirmLabel: result.fix.label,
    cancelLabel: 'Cancel',
  });
}

export type MapSaveDialogProps = {
  filenameDefault: string;
  askFilename: boolean;
  meta: MapMeta;
  onConfirm: (result: { filename: string | null; meta: MapMeta }) => void;
  onCancel: () => void;
};

export function MapSaveDialogPanel({
  filenameDefault,
  askFilename,
  meta: initialMeta,
  onConfirm,
  onCancel,
}: MapSaveDialogProps): JSX.Element {
  const [filename, setFilename] = useState(filenameDefault);
  const [meta, setMeta] = useState(initialMeta);

  const submit = (): void => {
    if (askFilename) {
      const name = sanitizeMapFilename(filename);

      if (!name) {
        onCancel();
        return;
      }

      onConfirm({ filename: name, meta: sanitizeMapMeta(meta) });
      return;
    }

    onConfirm({ filename: null, meta: sanitizeMapMeta(meta) });
  };

  return (
    <div class="editor-dialog-backdrop ui-chrome">
      <form
        class="editor-dialog editor-save-dialog ui-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Save map"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <h2 class="editor-dialog-title">Save map</h2>
        <p class="ui-hint">
          {askFilename
            ? 'Writes maps/<name>.json in this repo. Later Saves update that file without a dialog.'
            : 'Author credits are stored on the map file.'}
        </p>
        {askFilename ? (
          <label class="editor-dialog-field">
            <span class="ui-hint">File name</span>
            <input
              class="ui-number-input"
              type="text"
              name="map-file"
              value={filename}
              autofocus
              onInput={(event) => setFilename((event.target as HTMLInputElement).value)}
            />
          </label>
        ) : null}
        <MapMetaFields
          value={meta}
          onChange={(patch) => setMeta((current) => ({ ...current, ...patch }))}
          commit="input"
        />
        <div class="editor-dialog-actions">
          <button type="button" class="ui-button" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" class="ui-button" autofocus={!askFilename}>
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

export function promptMapSave(options: {
  filename: string;
  meta: MapMeta;
  askFilename: boolean;
}): Promise<{ filename: string | null; meta: MapMeta } | null> {
  return new Promise((resolve) => {
    const settle = (value: { filename: string | null; meta: MapMeta } | null): void => {
      setPanel('editorSaveDialog', null);
      resolve(value);
    };

    setPanel('editorSaveDialog', {
      filenameDefault: options.filename,
      askFilename: options.askFilename,
      meta: options.meta,
      onConfirm: (result) => settle(result),
      onCancel: () => settle(null),
    });
  });
}

/** Asks for a `maps/<name>.json` basename. Empty or invalid names cancel. */
export function promptMapFilename(defaultValue: string): Promise<string | null> {
  return new Promise((resolve) => {
    const settle = (value: string | null): void => {
      setPanel('editorDialog', null);
      resolve(value);
    };

    setPanel('editorDialog', {
      title: 'Save map',
      message: 'Writes maps/<name>.json in this repo. Later Saves update that file without a dialog.',
      confirmLabel: 'Save',
      cancelLabel: 'Cancel',
      inputDefault: defaultValue,
      inputLabel: 'File name',
      onConfirm: (value) => settle(sanitizeMapFilename(value)),
      onCancel: () => settle(null),
    });
  });
}
